import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {CostView} from "../../models/infix-formula/costView";
import {InfixFormula} from "../../models/infix-formula/infix-formula";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: "infix-formula-builder",
    templateUrl: "./formula-builder.component.html",
    styleUrls: ["./formula-builder.component.scss"],
})
export class InfixFormulaBuilderComponent implements OnChanges, OnInit {
    @Input() public costView: CostView;
    @Input() public propertiesLoaded: boolean = false;
    @Input() public formulaLoaded: boolean = true;
    @Input() public formulaValue: string = "";
    @Input() public forceReset: number;
    @Output() public formulaChange: EventEmitter<any> = new EventEmitter();

    public infixFormula: InfixFormula;
    public dragTrigger: any;

    constructor(public translateService: TranslateService) {}

    public ngOnInit() {
        this.infixFormula = new InfixFormula(this.formulaValue, this.costView, this.translateService);
        this.infixFormula.resetDrag();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (this.costView.propertiesLoaded && this.costView.formulaLoaded) {
            this.infixFormula = new InfixFormula(this.formulaValue, this.costView, this.translateService);
            this.infixFormula.resetDrag();
        }
        console.log(changes);
    }

    // Update the operators inside groups
    public updateInnerOperator(groupId, rowId, $event) {
        this.infixFormula.innerOperators[groupId][rowId - 1] = $event.target.value;
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    // Update the functions inside groups
    public updateInnerFunctions(groupId, rowId, $event) {
        this.costView.mathFunctions.some((func) => {
            if (func.value === $event.target.value) {
                this.infixFormula.innerFunctions[groupId][rowId] = func.value;
                return true;
            }
        });
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    public updateNestedInnerOperators(nestedId, nestedRowId, $event) {
        this.infixFormula.nestedInnerOperators[nestedId][nestedRowId - 1] = $event.target.value;
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    public updateNestedInnerFunctions(nestedId, nestedRowId, $event) {
        this.costView.mathFunctions.some((func) => {
            if (func.value === $event.target.value) {
                this.infixFormula.nestedInnerFunctions[nestedId][nestedRowId] = func.value;
                return true;
            }
        });
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    // Update the operators between groups
    public updateOutterOperator(groupId, $event) {
        this.infixFormula.outterOperators[groupId] = $event.target.value;
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    // Add a variable to a formula group
    public addVariable(groupId: number, event: any) {
        // console.log("---- addVariable", event, groupId);
        if (!event.data) { return false; }
        const dragData = Object.assign({}, event.data);
        let el = event.event.target.closest(".workarea__row");
        const dp = event.event.target.getAttribute("data-place");
        const childNodes = event.event.target.childNodes;
        let lastNested = false;
        let insertAfter = false;
        for (let i = childNodes.length - 1; i >= 0; i--) {
            if (childNodes[i].classList && childNodes[i].classList.contains("workarea__row")) {
                el = childNodes[i];
                lastNested = true;
                break;
            }
        }
        if (dp === "after") {
            insertAfter = true;
        }
        if (el) {
            const rowPosition = Number(el.getAttribute("data-rowid"));
            const nestedRowPosition = el.getAttribute("data-nestedrowid");

            // console.log(rowPosition, nestedRowPosition, this.infixFormula, insertAfter);
            if (nestedRowPosition !== "none") {
                // console.log("-- no nestedPosition");
                const nestedId = this.infixFormula.cfGroups[groupId][rowPosition].nestedId;
                this.infixFormula.nestedInnerFunctions[nestedId] = this.infixFormula.nestedInnerFunctions[nestedId] || [];
                if (lastNested) {
                    // console.log("-- no nestedPosition - last nested");
                    if (this.infixFormula.cfGroups[groupId][rowPosition].value.length) {
                        this.infixFormula.nestedInnerOperators[nestedId] = this.infixFormula.nestedInnerOperators[nestedId] || [];
                        this.infixFormula.nestedInnerOperators[nestedId].push("+");
                    }
                    this.infixFormula.nestedInnerFunctions[nestedId].push("");
                    this.infixFormula.cfGroups[groupId][rowPosition].value.push(dragData);
                } else {
                    // console.log("-- no nestedPosition - not last nested");
                    if (this.infixFormula.cfGroups[groupId][rowPosition].value.length) {
                        this.infixFormula.nestedInnerOperators[nestedId] = this.infixFormula.nestedInnerOperators[nestedId] || [];
                        this.infixFormula.nestedInnerOperators[nestedId].splice(insertAfter ? (nestedRowPosition + 1) : (nestedRowPosition), 0, "+");
                    }
                    this.infixFormula.nestedInnerFunctions[nestedId].splice(insertAfter ? (nestedRowPosition + 1) : (nestedRowPosition), 0, "");
                    this.infixFormula.cfGroups[groupId][rowPosition].value.splice(insertAfter ? (nestedRowPosition + 1) : (nestedRowPosition), 0, dragData);
                }
            } else {
                // console.log("-- nestedPosition");
                if (lastNested) {
                    // console.log("-- nestedPosition - last nested");
                    this.infixFormula.cfGroups[groupId].push(dragData);
                    this.infixFormula.innerOperators[groupId].push("+");
                    this.infixFormula.innerFunctions[groupId].push("");
                } else {
                    // console.log("-- nestedPosition - no last nested");
                    if (this.infixFormula.cfGroups[groupId].length) {
                        this.infixFormula.innerOperators[groupId].splice(insertAfter ? (rowPosition + 1) : rowPosition, 0, "+");
                    }
                    this.infixFormula.innerFunctions[groupId].splice(insertAfter ? (rowPosition + 1) : rowPosition, 0, "");
                    this.infixFormula.cfGroups[groupId].splice(insertAfter ? (rowPosition + 1) : rowPosition, 0, dragData);
                }
            }
        } else {
            this.infixFormula.cfGroups[groupId].push(dragData);
            this.infixFormula.innerOperators[groupId].push("+");
            this.infixFormula.innerFunctions[groupId].push("");
        }
        this.removeClass(".workarea__row", "workarea__row--hover");
        this.removeClass(".workarea__dropzone", "drag-over");
        this.removeClass(".dropzone_area", "readyForDrop");
        this.formulaChange.emit(this.infixFormula.toInfix());

    }

    // Remove variable from groups
    public removeVariable(groupId: number, rowId: number, nestedRowId: number = undefined, emitChange: boolean = true) {
        // nestedRowId is transmitted only when the group is nested
        if (!nestedRowId && Number(nestedRowId) !== 0) {
            this.infixFormula.cfGroups[groupId].splice(rowId, 1);
            this.infixFormula.innerFunctions[groupId].splice(rowId, 1);
            if (this.infixFormula.cfGroups[groupId].length) {
                this.infixFormula.innerOperators[groupId].splice(rowId - 1 > 0 ? rowId - 1 : 0, 1);
            }
        } else {
            const nestedId = this.infixFormula.cfGroups[groupId][rowId].nestedId;
            this.infixFormula.cfGroups[groupId][rowId].value.splice(nestedRowId, 1);
            this.infixFormula.nestedInnerFunctions[nestedId].splice(nestedRowId, 1);
            if (this.infixFormula.cfGroups[groupId][rowId].value.length) {
                this.infixFormula.nestedInnerOperators[nestedId].splice(nestedRowId - 1 > 0 ? nestedRowId - 1 : 0, 1);
            }
        }
        if (emitChange) { this.formulaChange.emit(this.infixFormula.toInfix()); }
    }

    // Add a new group
    public addGroup(groupId: number) {
        this.infixFormula.cfGroups.splice(groupId + 1, 0, []);
        this.infixFormula.innerOperators.splice(groupId + 1, 0, []);
        this.infixFormula.innerFunctions.splice(groupId + 1, 0, []);
        if (this.costView.formulaType === "formula") {
            this.infixFormula.outterOperators.splice(groupId + 1, 0, "+");
        } else {
            this.infixFormula.outterOperators.splice(groupId + 1, 0, "OR");
        }
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    // Add a nested group
    public addNestedGroup(groupId: number) {
        if (this.infixFormula.cfGroups[groupId].length) { this.infixFormula.innerOperators[groupId].push("+"); }
        this.infixFormula.cfGroups[groupId].push({
            label: "",
            name: "group",
            value: [],
            type: "group",
            nestedId: this.infixFormula.nestedGroupId,
        });
        this.infixFormula.nestedInnerOperators[this.infixFormula.nestedGroupId] = this.infixFormula.nestedInnerOperators[this.infixFormula.nestedGroupId] || [];
        this.infixFormula.nestedInnerFunctions[this.infixFormula.nestedGroupId] = this.infixFormula.nestedInnerFunctions[this.infixFormula.nestedGroupId] || [];
        this.infixFormula.innerFunctions[groupId].push("");
        this.infixFormula.nestedGroupId++;
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    // Remove this group
    public removeGroup(groupId: number, rowId: number = undefined) {
        // rowId is transmitted only when the group is nested
        if (!rowId) {
            this.infixFormula.cfGroups.splice(groupId, 1);
            this.infixFormula.outterOperators.splice(groupId - 1, 1); // since first group doesnt have outterOperator
            this.infixFormula.innerOperators.splice(groupId, 1);
            this.infixFormula.innerFunctions.splice(groupId, 1);
        } else {
            this.infixFormula.cfGroups[groupId].splice(rowId, 1);
            this.infixFormula.innerFunctions[groupId].splice(rowId, 1);
            if (this.infixFormula.cfGroups[groupId].length) {
                this.infixFormula.innerOperators[groupId].splice(rowId - 1 > 0 ? rowId - 1 : 0, 1);
            }
        }
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    // Event that fires when user starts dragging a variable from INSIDE the formula
    public onDragStart(event, groupId, rowId, nestedRowId) {
        event.dataTransfer.setData("groupId", groupId);
        event.dataTransfer.setData("rowId", rowId);
        event.dataTransfer.setData("nestedRowId", nestedRowId);
    }

    // On drop on an element from the formula
    // (checks too see that it's from INSIDE the formula)
    public onDrop(event, destinationGroupId, destinationRowId, destinationNestedRowId) {
        // console.log("---- onDrop", event, destinationGroupId, destinationRowId, destinationNestedRowId);
        event.target.style.opacity = null;
        const originGroupId = event.dataTransfer.getData("groupId");
        const originRowId = event.dataTransfer.getData("rowId");
        const originNestedRowId = event.dataTransfer.getData("nestedRowId");

        if (!originGroupId && !originRowId && !originNestedRowId) { return; }

        const nestedOrigin = typeof originGroupId !== "undefined" && typeof originRowId !== "undefined" && originNestedRowId !== "none";
        const nestedDestination = typeof destinationGroupId !== "undefined" && typeof destinationRowId !== "undefined" && destinationNestedRowId !== "none";
        const normalOrigin = typeof originGroupId !== "undefined" && typeof originRowId !== "undefined";
        const normalDestination = typeof destinationGroupId !== "undefined" && typeof destinationRowId !== "undefined";

        // console.log(nestedOrigin, nestedDestination, normalOrigin, normalDestination);

        if (nestedOrigin) {
            if (nestedDestination) {
                const draggedRow = this.infixFormula.cfGroups[originGroupId][originRowId].value[originNestedRowId];
                this.removeVariable(originGroupId, originRowId, originNestedRowId, false);
                const nestedId = this.infixFormula.cfGroups[destinationGroupId][destinationRowId].nestedId;
                if (this.infixFormula.cfGroups[destinationGroupId][destinationRowId].value.length) {
                    this.infixFormula.nestedInnerOperators[nestedId] = this.infixFormula.nestedInnerOperators[nestedId] || [];
                    this.infixFormula.nestedInnerOperators[nestedId].splice(destinationNestedRowId - 1, 0, "+");
                }
                this.infixFormula.nestedInnerFunctions[nestedId] = this.infixFormula.nestedInnerFunctions[nestedId] || [];
                this.infixFormula.nestedInnerFunctions[nestedId].splice(destinationNestedRowId, 0, "");
                this.infixFormula.cfGroups[destinationGroupId][destinationRowId].value.splice(destinationNestedRowId, 0, draggedRow);
            } else if (normalDestination) {
                const draggedRow = this.infixFormula.cfGroups[originGroupId][originRowId].value[originNestedRowId];
                this.removeVariable(originGroupId, originRowId, originNestedRowId, false);
                if (this.infixFormula.cfGroups[destinationGroupId].length) {
                    this.infixFormula.innerOperators[destinationGroupId].splice(destinationRowId - 1, 0, "+");
                    this.infixFormula.innerFunctions[destinationGroupId].splice(destinationRowId, 0, "");
                }
                this.infixFormula.cfGroups[destinationGroupId].splice(destinationRowId, 0, draggedRow);
            }
        } else if (normalOrigin) {
            if (nestedDestination) {
                const draggedRow = this.infixFormula.cfGroups[originGroupId][originRowId];
                const draggedRowOperator = this.infixFormula.innerOperators[originGroupId][originRowId - 1]; // if we want to drag oper&func
                const draggedRowFunction = this.infixFormula.innerFunctions[originGroupId][originRowId]; // not used atm
                // Add the row to the new location
                const nestedId = this.infixFormula.cfGroups[destinationGroupId][destinationRowId].nestedId;
                if (this.infixFormula.cfGroups[destinationGroupId][destinationRowId].value.length) {
                    this.infixFormula.nestedInnerOperators[nestedId] = this.infixFormula.nestedInnerOperators[nestedId] || [];
                    this.infixFormula.nestedInnerOperators[nestedId].splice(destinationNestedRowId, 0, "+");
                }
                this.infixFormula.nestedInnerFunctions[nestedId] = this.infixFormula.nestedInnerFunctions[nestedId] || [];
                this.infixFormula.nestedInnerFunctions[nestedId].splice(destinationNestedRowId + 1, 0, "");
                this.infixFormula.cfGroups[destinationGroupId][destinationRowId].value.splice(destinationNestedRowId + 1, 0, draggedRow);
                // Remove the dragged row from original position
                this.removeVariable(originGroupId, originRowId, undefined, false);

                // TO DO WHEN NESTED DESTINATION IS EMPTY !!!

            } else if (normalDestination) {
                const draggedRow = this.infixFormula.cfGroups[originGroupId][originRowId];
                const draggedRowOperator = this.infixFormula.innerOperators[originGroupId][originRowId - 1];
                const draggedRowFunction = this.infixFormula.innerFunctions[originGroupId][originRowId];
                // Remove the dragged row from original position
                this.removeVariable(originGroupId, originRowId, undefined, false);
                // Add the row to the new location
                if (this.infixFormula.cfGroups[destinationGroupId].length) {
                    this.infixFormula.innerOperators[destinationGroupId].splice(destinationRowId - 1, 0, draggedRowOperator);
                    this.infixFormula.innerFunctions[destinationGroupId].splice(destinationRowId, 0, draggedRowFunction);
                }
                this.infixFormula.cfGroups[destinationGroupId].splice(destinationRowId, 0, draggedRow);
            }
        }
        this.removeClass(".workarea__row", "workarea__row--hover");
        this.removeClass(".workarea__dropzone", "drag-over");
        this.removeClass(".dropzone_area", "readyForDrop");
        this.formulaChange.emit(this.infixFormula.toInfix());
        event.preventDefault();
    }

    public allowDrop(event) {
        event.preventDefault();
    }

    public getName(item) {
      item = this.stripHtml(item);
      item = item.replace(/\{\{data\./g, "{{");
      return item.substr(item.lastIndexOf(".") + 1);
    }

    private stripHtml(value: string): string {
        if (!value) { return value; }
        return value.replace(/<[^>]*>/g, "");
    }

    public notPresetVariable(name) {
       return name !== "numericValue" && name !== "textValue" && name !== "dateValue";
    }

    public enterDropZone(event) {
        event.target.classList.add("readyForDrop");
    }

    public leaveDropZone(event) {
        event.target.classList.remove("readyForDrop");
    }

    // While dragging, on hover, add/remove a style to that el
    public addStyle(event) {
        // event.target.style.opacity = 0.5;
        this.removeClass(".workarea__row", "workarea__row--hover");
        this.dragTrigger = event.target;
        if (!event.target.closest(".workarea__dropzone").classList.contains("drag-over")) {
            this.removeClass(".workarea__dropzone", "drag-over");
            event.target.closest(".workarea__dropzone").classList.add("drag-over");
        }
        const row = event.target.closest(".workarea__row");
        if (row) { row.classList.add("workarea__row--hover"); }
    }

    public removeStyle(event) {
        // event.target.style.opacity = null;
        if (event.target === this.dragTrigger && event.target.closest(".workarea__row")) {
            this.removeClass(".workarea__row", "workarea__row--hover");
        }
    }

    public addStyleDrop(event, groupId, rowId = undefined) {
        event.preventDefault();
        /*
        this.removeClass(".workarea__dropzone", "drag-over");
        if (!rowId && event.target.closest(".workarea__dropzone")) {
            event.target.closest(".workarea__dropzone").classList.add("drag-over");
            return;
        }
        */
    }

    public saveFormula() {
        return this.infixFormula.toInfix();
    }

    public clearFormula() {
        this.infixFormula.initProperties();
        this.formulaChange.emit(this.infixFormula.toInfix());
    }

    public removeClass(selector, className) {
        const els = document.querySelectorAll(selector);
        for (let i = 0; i < els.length; i++) {
            els[i].classList.remove(className);
        }
    }

    public preSetVariableChanged() {
        this.formulaChange.emit(this.infixFormula.toInfix());
    }
}
